import { useMemo } from 'react';

import type { ToolsTableRow } from '../../types';

/**
 * Hook to compute registry statistics including enabled/disabled counts,
 * system tools detection, and function IDs for bulk operations
 */
export function useRegistryStats(toolRow: ToolsTableRow) {
  return useMemo(() => {
    if (!toolRow.isRegistry) {
      return {
        allEnabled: false,
        allDisabled: false,
        enabledCount: 0,
        totalCount: 0,
        functionIds: [],
        allSystemTools: false,
        hasSystemTools: false,
        hasNonSystemTools: false,
      };
    }

    const tools = toolRow.items.filter(item => !item.isRegistry && 'tool' in item && item.tool);
    const systemTools = tools.filter(item => 'tool' in item && item.tool.is_system_function);
    const nonSystemTools = tools.filter(item => 'tool' in item && !item.tool.is_system_function);
    const enabledTools = tools.filter(item => 'tool' in item && !item.tool.is_disabled);
    const functionIds = tools
      .filter(item => 'tool' in item)
      .map(item => ('tool' in item ? item.tool.function_id : ''));

    return {
      allEnabled: enabledTools.length === tools.length && tools.length > 0,
      allDisabled: enabledTools.length === 0,
      enabledCount: enabledTools.length,
      totalCount: tools.length,
      functionIds,
      allSystemTools: systemTools.length === tools.length && tools.length > 0,
      hasSystemTools: systemTools.length > 0,
      hasNonSystemTools: nonSystemTools.length > 0,
    };
  }, [toolRow]);
}

/**
 * Hook to determine if a specific tool is currently being updated
 */
export function useToolLoadingState(
  toolRow: ToolsTableRow,
  isUpdating: boolean,
  updatingToolId: string | null | undefined,
  isUpdatingEnabledInPrimaryAgent: boolean,
  updatingToolIdEnabledInPrimaryAgent: string | null | undefined
) {
  return useMemo(() => {
    if (toolRow.isRegistry) {
      return false;
    }

    return (
      (isUpdating && updatingToolId === toolRow.tool.function_id) ||
      (isUpdatingEnabledInPrimaryAgent &&
        updatingToolIdEnabledInPrimaryAgent === toolRow.tool.function_id)
    );
  }, [
    toolRow,
    isUpdating,
    updatingToolId,
    isUpdatingEnabledInPrimaryAgent,
    updatingToolIdEnabledInPrimaryAgent,
  ]);
}

/**
 * Hook to generate contextual tooltip content for registry toggle
 */
export function useRegistryTooltip(
  allSystemTools: boolean,
  hasSystemTools: boolean,
  hasNonSystemTools: boolean,
  totalCount: number,
  allEnabled: boolean,
  allDisabled: boolean,
  enabledCount: number
) {
  return useMemo(() => {
    if (allSystemTools) {
      return 'This registry contains only system tools that cannot be disabled.';
    }
    if (!hasNonSystemTools) {
      return 'No toggleable tools in this registry';
    }
    if (totalCount === 0) {
      return 'No tools in this registry';
    }
    if (hasSystemTools && hasNonSystemTools) {
      return 'This registry contains system tools that cannot be disabled. Only non-system tools will be affected.';
    }
    if (allEnabled) {
      return 'Disable all tools in this registry';
    }
    if (allDisabled) {
      return 'Enable all tools in this registry';
    }
    return `${enabledCount} of ${totalCount} tools enabled. Click to disable all.`;
  }, [
    allSystemTools,
    hasSystemTools,
    hasNonSystemTools,
    totalCount,
    allEnabled,
    allDisabled,
    enabledCount,
  ]);
}
